import { useRef, useState } from 'react'
import { Dialog } from 'radix-ui'
import { Plus, Upload, X } from 'lucide-react'
import { Combobox } from '@/components/common/Combobox'
import { useDocumentTypes } from '@/features/admin/document-types/hooks'
import { useUploadFile } from '../hooks'

const ALLOWED_ACCEPT =
  '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,image/jpeg,image/png,image/gif,image/webp'
const MAX_SIZE = 50 * 1024 * 1024

export function FileUploadButton({ contractId, configurationCode }: { contractId: number; configurationCode: string }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded bg-sky-600 px-3 py-1.5 text-sm text-white hover:bg-sky-700 whitespace-nowrap"
      >
        <Plus className="size-3.5" />
        <span>Dodaj plik</span>
      </button>
      {open && (
        <UploadDialog
          contractId={contractId}
          configurationCode={configurationCode}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  )
}

function UploadDialog({ contractId, configurationCode, onClose }: {
  contractId: number
  configurationCode: string
  onClose: () => void
}) {
  const upload = useUploadFile(contractId)
  const typesQ = useDocumentTypes(configurationCode, true)
  const inputRef = useRef<HTMLInputElement>(null)
  const [file, setFile] = useState<File | null>(null)
  const [documentTypeId, setDocumentTypeId] = useState<string>('')
  const [description, setDescription] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)

  const setSelectedFile = (f: File | null) => {
    if (!f) { setFile(null); return }
    if (f.size > MAX_SIZE) {
      setError(`Plik za duży (${formatSize(f.size)}). Limit: 50 MB.`)
      setFile(null)
      return
    }
    setError(null)
    setFile(f)
  }

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedFile(e.target.files?.[0] ?? null)
  }

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    setDragOver(false)
    setSelectedFile(e.dataTransfer.files?.[0] ?? null)
  }

  const submit = () => {
    if (!file) { setError('Najpierw wybierz plik.'); return }
    setError(null)
    upload.mutate(
      {
        file,
        documentTypeId: documentTypeId ? Number(documentTypeId) : null,
        description: description || null,
      },
      {
        onSuccess: () => onClose(),
        onError: (err) => setError(formatError(err)),
      },
    )
  }

  const isSubmitting = upload.isPending

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o && !isSubmitting) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[520px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Dodaj plik</Dialog.Title>
          <Dialog.Description className="text-xs text-slate-500 mt-1">
            Załączniki PDF/DOC/Excel + obrazki, max 50 MB.
          </Dialog.Description>

          <div className="mt-4 flex flex-col gap-3 text-sm">
            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Typ dokumentu</span>
              <Combobox<string>
                value={documentTypeId || null}
                onChange={v => setDocumentTypeId(v ?? '')}
                options={(typesQ.data ?? []).map(t => ({ value: String(t.id), label: t.labelPl }))}
                emptyOptionLabel="- bez typu -"
                placeholder="- bez typu -"
                disabled={isSubmitting || typesQ.isLoading}
              />
            </label>

            <input
              ref={inputRef}
              type="file"
              accept={ALLOWED_ACCEPT}
              className="hidden"
              onChange={onPick}
              disabled={isSubmitting}
            />

            <div
              onClick={() => !isSubmitting && inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              className={[
                'rounded border-2 border-dashed p-6 text-center cursor-pointer transition-colors',
                dragOver ? 'border-sky-500 bg-sky-50' : 'border-slate-300 hover:border-slate-400 hover:bg-slate-50',
                isSubmitting ? 'opacity-50 cursor-not-allowed' : '',
              ].join(' ')}
            >
              {file ? (
                <div className="flex items-center justify-between gap-2">
                  <div className="flex flex-col gap-0.5 min-w-0 text-left">
                    <span className="font-medium truncate" title={file.name}>{file.name}</span>
                    <span className="text-xs text-slate-500">{formatSize(file.size)} · {file.type || 'unknown'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setFile(null) }}
                    className="text-slate-400 hover:text-slate-700"
                    disabled={isSubmitting}
                    aria-label="Usuń wybór"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1 text-slate-500">
                  <Upload className="size-6" />
                  <p className="text-sm">Przeciągnij plik tutaj albo <span className="text-sky-700 underline">wybierz z dysku</span></p>
                  <p className="text-[10px]">PDF, DOC(X), XLS(X), JPG, PNG, GIF, WEBP · max 50 MB</p>
                </div>
              )}
            </div>

            <label className="grid gap-1">
              <span className="text-xs uppercase text-slate-500">Opis (opcjonalny)</span>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={isSubmitting}
                className="border rounded px-2 py-1"
                placeholder="np. skan strony 1-3"
              />
            </label>

            {error && <p className="text-red-600 text-xs">{error}</p>}

            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-3 py-1.5 border rounded"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={isSubmitting || !file}
                className="px-3 py-1.5 bg-sky-600 text-white rounded disabled:opacity-50"
              >
                {isSubmitting ? 'Wysyłanie...' : 'Wyślij'}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`
}

function formatError(err: unknown): string {
  const ax = err as { response?: { data?: { detail?: string; title?: string } } }
  return ax.response?.data?.detail ?? ax.response?.data?.title ?? 'Błąd uploadu'
}
